/* eslint-disable no-underscore-dangle */
import { $t } from '@/i18n';
/**
 * 编辑模式的路由
 */
import Vue from 'vue';
import { message } from '@utils/wuji-message';
import qs from 'query-string';
import { cloneDeep, camelCase, upperFirst, isNil } from 'lodash';

import stringInterop from '@tencent/ui-core/lib/utils/stringInterop';

import RuntimeLayout from '@pages/app/runtimeLayout/Layout.vue';
import Page from '@pages/app/index.vue';
import DataTable from '@pages/runtime/dataTable';
import NotFound from '@pages/notFound/notFound.vue';
import Login from '@pages/login/index.vue';
import PermissionDenied from '@pages/PermissionDenied.vue';
import builtinRoute from './runtimeBuiltInRoute';
import store from '@store';
import { getBaseAppPath, isRegexPath, getWithoutNilQuery } from '@utils/path';
import { isAIPage, isDataTablePage } from '@/loaders/page/helper';
import { isOffline } from '@/utils/offline';

import GogoRuntimeRouterWrapper from '@/modules/ai-page/GogoRuntimeRouterWrapper.vue';
import GogoRuntimeRouterView from '@/modules/ai-page/GogoRuntimeRouterView.vue';
import { setVue3RouterBaseInfo } from '@/modules/ai-page/vue3router';

const getAppPageList = async ({ projectId, env }) => {
  let appInfo = {};
  let pageList = [];

  try {
    // 合并 project 和 page 数据请求
    // 不能直接用 window.xy_runtime_project, dispatch 里有对部分数据做格式化/兼容处理
    [appInfo, pageList] = await Promise.all([
      store.dispatch('runtime/getProject', { projectId, env }),
      store.dispatch('runtime/getBasicPageList', { projectId, env }),
    ]);
  } catch (e) {
    message.error($t('k_93102939', { arg_0: e.message }));
  }

  return { appInfo, pageList };
};

const buildRouteMeta = ({ project, page, pages }) => {
  const pageAttr = page.pageAttr ?? {};
  const { customNav, customSidebar } = pageAttr;

  const renderNavId = (isNil(customNav) || customNav === 'system') ? project?.advanceConfig?.customNav : customNav;
  if (renderNavId && renderNavId !== 'default') {
    const target = pages.find(p => p.pageId === renderNavId);
    pageAttr.height = target?.pageAttr?.height;
  }

  const renderSidebarId = (isNil(customSidebar) || customSidebar === 'system') ? project?.advanceConfig?.customSidebar : customSidebar;
  if (renderSidebarId && renderSidebarId !== 'default') {
    const target = pages.find(p => p.pageId === renderSidebarId);
    pageAttr.width = target?.pageAttr?.width;
  }

  return {
    pageId: page.pageId,
    pageName: page.name,
    pageAttr,
    isKeepAlive: pageAttr?.keepAlive,
    layoutType: page.layoutType,
    pageType: page.pageType || 'page',
  };
};

// 根据页面 ID 生成运行时的组件名
function getComponentName(prefix, pageId) {
  return `${prefix}${upperFirst(camelCase(pageId))}`.replace(/_/g, '-');
}

// 如果有 gogoDebugBridge 参数，则开启 hijack 模式
// 主要针对AI页面和低码页面冲突的问题
const gogoDebugBridge = qs.parse(window.location.search).wujiAiPageUK;
let GogoHijackLowcodePageRouterComponent = null;
if (gogoDebugBridge) {
  window.__gogoDebugHijack = Vue.observable({
    currentRouteIsGogoPage: '',
    isPicking: false,
  });
  GogoHijackLowcodePageRouterComponent = Vue.extend({
    name: 'GogoHijackLowcodePageRouterComponent',
    inheritAttrs: false,
    created() {
      if (qs.parse(window.location.search).iframe === '1') {
        import('@/styles/gogo-debug-hijack.scss');
        const warnElement = document.createElement('div');
        warnElement.className = 'gogo-debug-hijack-notice';
        warnElement.innerText = $t('k_f98a3b9e');

        this.warnElement = warnElement;
        this.$watch(
          () => !!window.__gogoDebugHijack?.isPicking && !window.__gogoDebugHijack.currentRouteIsGogoPage,
          (shallShowWarn) => {
            if (shallShowWarn) document.body.appendChild(warnElement);
            else warnElement.remove();
          },
          { immediate: true },
        );
      }
    },
    beforeDestroy() {
      this.warnElement?.remove();
    },
    render(h) {
      const matched = window.__gogoDebugHijack.currentRouteIsGogoPage;
      if (matched) {
        // 虽然原本这是一个低代码页面，但是在预览模式下path被AI页面占据了
        // 所以改成渲染AI页面
        return h(GogoRuntimeRouterView, {
          props: this.$attrs,
          on: this.$listeners,
        });
      }

      // const path = this.$options['x-gogo-path'];
      const component = this.$options['x-gogo-original-component'];
      return h(component, { props: this.$attrs, on: this.$listeners });
    },
  });
}

export default async ({ projectId, env, branch }) => {
  store.commit('runtime/setMode', 'runtime');
  const routerArray = cloneDeep([builtinRoute.route]);

  const base = getBaseAppPath({ env, projectId, branch });
  setVue3RouterBaseInfo({
    base, branch, env, projectId,
  });

  const { appInfo, pageList } = await getAppPageList({ projectId, env });
  Vue.prototype.$project = { appInfo, pageList };

  const hasGogo = !!gogoDebugBridge || pageList.some(page => page.type === 'ai');
  const $gogoRuntimeRouterWrapperProps = Promise.resolve({ projectId, env, branch });

  const pageNormalRoute = []; // 普通路径
  const pageRegexRoute = []; // 正则表达式路径

  pageList.forEach((page) => {
    let component;
    const name = page.pageId;

    if (isDataTablePage(page)) {
      component = Vue.extend({ ...DataTable, name: getComponentName('DataTable', page.pageId) });
    } else if (isAIPage(page)) {
      component = GogoRuntimeRouterView;
    } else {
      component = Vue.extend({ ...Page, name: getComponentName('RuntimePage', page.pageId) });
    }

    if (GogoHijackLowcodePageRouterComponent && component !== GogoRuntimeRouterView) {
      // 这个页面可能需要被特殊处理下
      const originalComponent = component;
      component = GogoHijackLowcodePageRouterComponent.extend({
        'x-gogo-original-component': originalComponent,
        'x-gogo-path': page.path,
      });
    }

    pushRoute(page.path, name, page, component);
    if (isOffline()) {
      pushRoute(`${page.path}.html`, `${page.pageId}_offline`, page, component);
    }
  });

  function pushRoute(path, name, page, component) {
    const route = {
      path,
      name,
      component,
      props: { projectId, pageId: page.pageId },
      meta: buildRouteMeta({ project: appInfo, page, pages: pageList }),
    };
    if (isRegexPath(page.path)) {
      pageRegexRoute.push(route);
    } else {
      pageNormalRoute.push(route);
    }
  }

  // [vue-router] 同一个路径可以匹配多个路由时，匹配的优先级就按照路由的定义顺序：路由定义得越早，优先级就越高。
  // 这里需要把正则表达式的路径放在后面定义, 让普通路径的页面优先命中
  routerArray.push(...pageNormalRoute, ...pageRegexRoute);

  // 流程引擎中的默认页面片是以 !generated: 开头的
  routerArray.push({
    path: '/!generated:*/',
    name: 'generate',
    component: Page,
    props(route) {
      const [pageId] = route.path.match(/!generated:.+/);
      return { projectId, pageId: decodeURIComponent(pageId) };
    },
  });

  // 默认根路径
  const rootPath = '/';
  if (routerArray.every(route => route.path !== rootPath)) {
    const rootRoute = { path: '/' };
    if (appInfo?.homePage?.path) {
      rootRoute.redirect = () => {
        // Query 参数支持插值表达式
        const query = qs.parse(stringInterop(appInfo.homePage.query || '', {
          ...window?.w?.mainRenderer ?? {},
          $app: Vue.prototype.$app,
          w: window?.w,
        }));
        return {
          path: appInfo.homePage.path,
          query: getWithoutNilQuery(query),
        };
      };
    } else if (GogoHijackLowcodePageRouterComponent) {
      // 开发模式下 + 没有配置自定义的主页，则根路径随时可能会被AI页面接管
      rootRoute.component = GogoHijackLowcodePageRouterComponent.extend({
        'x-gogo-path': '/',
        'x-gogo-original-component': {
          name: 'RuntimePageBlankHome',
          render() {
            return null;
          },
        },
      });
    }
    routerArray.unshift(rootRoute);
  }

  // 403
  const PATH_403 = '/403';
  if (routerArray.every(route => route.path !== PATH_403)) {
    routerArray.push({
      path: PATH_403,
      component: PermissionDenied,
    });
  }

  // 404
  const PATH_404 = '/404';
  if (routerArray.every(route => route.path !== PATH_404)) {
    routerArray.push({
      path: PATH_404,
      component: NotFound,
    });
  }

  routerArray.push({
    path: '/login',
    component: Login,
  });

  // 兼容小程序智能网关跳转过来的 path
  routerArray.push({
    path: '/an:*',
    redirect: to => `/${to.path.split('/').pop()}`,
  });

  // 通配符
  const PATH_WILDCARD = '*';
  const HAS_NO_WILDCARD_PAGE = routerArray.every(route => route.path !== PATH_WILDCARD);
  if (hasGogo) {
    if (HAS_NO_WILDCARD_PAGE) console.warn('Project is using AI page, hence wildcard route of LowCode page is unavailable');
    routerArray.push({
      path: PATH_WILDCARD,
      component: GogoRuntimeRouterView,
      props: {
        projectId,
        env,
        branch,
      },
    });
  } else if (HAS_NO_WILDCARD_PAGE) {
    routerArray.push({
      path: PATH_WILDCARD,
      redirect: '/404',
    });
  }

  // 加载 WujiGogo 的情况
  // 具体和 Designer 相关的逻辑都在
  let AppLayout = RuntimeLayout;
  if (hasGogo) {
    const props = await $gogoRuntimeRouterWrapperProps;
    AppLayout = {
      functional: true,
      render(createElement, context) {
        return createElement(
          GogoRuntimeRouterWrapper,
          { props },
          [createElement(
            RuntimeLayout,
            context.data,
            context.children,
          )],
        );
      },
    };
  }

  return {
    mode: 'history',
    base,
    routes: [
      {
        path: '/',
        component: AppLayout,
        props: {
          appConfig: appInfo,
          env,
          mode: 'runtime',
        },
        children: routerArray,
      },
    ],
  };
};
